"use client";

import { useCallback, useEffect, useRef, type FC } from "react";
import { MarkerClusterer, type Cluster, type Renderer } from "@googlemaps/markerclusterer";
import { AdvancedMarker, InfoWindow, useMap } from "@vis.gl/react-google-maps";
import type { PractitionerPublicProfileDto } from "cmw-client";
import { CmwMark } from "@/components/logo";
import { PractitionerCard } from "@/components/practitioner-card";
import { cn } from "@/lib/cn";
import type { Coordinates } from "../../lib/map-options";

export type PinnedPractitioner = {
  id: string;
  practitioner: PractitionerPublicProfileDto;
  coordinates: Coordinates;
};

const clusterElement = (count: number) => {
  const isLarge = count >= 10;

  const wrapper = document.createElement("div");
  wrapper.className = "flex flex-col items-center";

  const shell = document.createElement("div");
  shell.className = cn(
    "flex items-center justify-center rounded-circle border-3 border-brand-blue bg-[#fdfbf9]",
    "drop-shadow-[0px_3.2px_3.68px_rgba(176,204,200,0.51)]",
    isLarge ? "size-96" : "size-76",
  );

  const disc = document.createElement("span");
  disc.className = cn(
    "flex shrink-0 items-center justify-center rounded-circle bg-brand-blue font-semibold text-neutral-white",
    isLarge ? "size-72 text-32" : "size-52 text-24",
  );
  disc.textContent = String(count);

  const tail = document.createElement("span");
  tail.className = "-mt-[3px] size-0 border-x-[9px] border-t-[12px] border-x-transparent border-t-brand-blue";

  shell.append(disc);
  wrapper.append(shell, tail);
  return wrapper;
};

const clusterRenderer: Renderer = {
  render: ({ count, position }: Cluster) =>
    new google.maps.marker.AdvancedMarkerElement({
      position,
      content: clusterElement(count),
      zIndex: 99_999 + count,
    }),
};

const PractitionerPin: FC<{ isActive?: boolean }> = ({ isActive }) => (
  <div className="flex flex-col items-center">
    <div
      className={cn(
        "flex items-center justify-center rounded-circle border-2 border-brand-blue bg-neutral-white",
        "shadow-[0px_3.2px_3.68px_rgba(176,204,200,0.51)] transition-[width,height] duration-150",
        isActive ? "size-60" : "size-50",
      )}
    >
      <CmwMark className={cn("transition-[width,height] duration-150", isActive ? "size-34" : "size-28")} />
    </div>
    <span
      aria-hidden
      className={cn(
        "-mt-px size-0 border-x-[7px] border-t-[9px] border-x-transparent border-t-brand-blue",
        "drop-shadow-[0px_2px_1px_rgba(176,204,200,0.4)]",
      )}
    />
  </div>
);

type PractitionerMarkersProps = {
  pinned: PinnedPractitioner[];
  shown: PinnedPractitioner | null;
  activeId?: string;
  onHover: (pin: PinnedPractitioner | null) => void;
  onSelect: (pin: PinnedPractitioner) => void;
  onDismiss?: () => void;
};

export const PractitionerMarkers: FC<PractitionerMarkersProps> = ({
  pinned,
  shown,
  activeId,
  onHover,
  onSelect,
  onDismiss,
}) => {
  const map = useMap();
  const clustererRef = useRef<MarkerClusterer | null>(null);
  const markersRef = useRef(new Map<string, google.maps.marker.AdvancedMarkerElement>());
  const frameRef = useRef<number | null>(null);

  const syncClusterer = useCallback(() => {
    if (frameRef.current !== null) return;

    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      const clusterer = clustererRef.current;
      if (!clusterer) return;

      clusterer.clearMarkers(true);
      clusterer.addMarkers([...markersRef.current.values()]);
    });
  }, []);

  const setMarkerRef = useCallback(
    (marker: google.maps.marker.AdvancedMarkerElement | null, id: string) => {
      const markers = markersRef.current;

      if (marker) {
        if (markers.get(id) === marker) return;
        markers.set(id, marker);
      } else {
        if (!markers.has(id)) return;
        markers.delete(id);
      }

      syncClusterer();
    },
    [syncClusterer],
  );

  useEffect(() => {
    if (!map) return;

    clustererRef.current = new MarkerClusterer({ map, renderer: clusterRenderer });
    syncClusterer();

    return () => {
      clustererRef.current?.clearMarkers();
      clustererRef.current?.setMap(null);
      clustererRef.current = null;
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    };
  }, [map, syncClusterer]);

  return (
    <>
      {pinned.map((pin) => {
        const isActive = activeId === pin.id;

        return (
          <AdvancedMarker
            key={pin.id}
            ref={(marker) => setMarkerRef(marker, pin.id)}
            position={pin.coordinates}
            zIndex={isActive ? 9_999 : 999}
            onClick={() => onSelect(pin)}
            onMouseEnter={() => onHover(pin)}
            onMouseLeave={() => onHover(null)}
          >
            <PractitionerPin isActive={isActive} />
          </AdvancedMarker>
        );
      })}

      {/* The focused InfoWindow swallows Escape and closes itself; closeclick is how we hear about it */}
      {shown && (
        <InfoWindow
          position={shown.coordinates}
          pixelOffset={[0, -64]}
          disableAutoPan
          headerDisabled
          onCloseClick={onDismiss}
        >
          <PractitionerCard source={shown.practitioner} variant="map-hover-card" />
        </InfoWindow>
      )}
    </>
  );
};
